import React, { useCallback, useMemo } from 'react';
import { CellContext } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { CompanyMembershipDto } from '@area-restrita/api-client/model/companyMembershipDto';
import { useUpdateCompanyMembership } from '@area-restrita/api-client/endpoints/company-memberships/company-memberships.gen';

import CompanyDepartmentSelector from '@/components/QuerySelectors/CompanyDepartmentSelector';
import MutationStatusIndicator from '@/components/MutationStatusIndicator';
import { SelectOption } from '@/components/Select';
import { useCompanyContext } from '@/contexts/CompanyContext';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

type IProps = CellContext<CompanyMembershipDto, unknown>;

const UserDepartmentsCell: React.FC<IProps> = ({ row }) => {
  const { companyId } = useCompanyContext();
  const { checkAuthorization } = useAuthSessionStore();
  const { departments, user } = row.original;
  const queryClient = useQueryClient();

  const value = useMemo(
    () => departments.map(({ id, name }) => ({ label: name, value: id })),
    [departments],
  );

  const updateCompanyMembership = useUpdateCompanyMembership({
    mutation: {
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: [`/api/v1/companies/${companyId}/memberships`],
        });
      },
    },
  });

  const handleChange = useCallback(
    (value: SelectOption[]) =>
      updateCompanyMembership.mutateAsync({
        userId: user.id,
        companyId,
        data: {
          departments: value.map(({ value }) => value as number),
        },
      }),
    [updateCompanyMembership, user.id, companyId],
  );

  return (
    <CompanyDepartmentSelector
      multiple
      value={value}
      icon={<MutationStatusIndicator mutation={updateCompanyMembership} />}
      onChange={handleChange}
      disableOnSingle
      readOnly={checkAuthorization(({ cannot }) =>
        cannot('update', 'company_memberships'),
      )}
    />
  );
};

export default UserDepartmentsCell;
